import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import Loader from "components/Loader";
import { CLIENT_MODULE_ENTRIES_QUERY_KEY, fetchModuleEntries } from "./clientDocuments.api";
import { groupEntriesByModule, moduleLabel } from "./clientDocuments.utils";

const entryKey = (entry) => `${entry.moduleType}:${entry.entryId}`;

/**
 * Picks the module entries a collected document should also surface in. Ticking is additive and
 * unticking removes the link; the file itself is never copied or moved by either.
 */
const ShowInModuleDialog = ({
  open,
  clientId,
  document,
  existingLinks,
  loading,
  onCancel,
  onSave,
}) => {
  const [selected, setSelected] = useState(new Set());

  const query = useQuery({
    queryKey: [CLIENT_MODULE_ENTRIES_QUERY_KEY, clientId],
    queryFn: () => fetchModuleEntries(clientId),
    enabled: Boolean(open && clientId),
    refetchOnMount: "always",
  });

  // Compared by value, not identity: existingLinks is rebuilt by the parent on every render, and
  // depending on the array itself would reseed the ticks mid-interaction and undo every click.
  const existingKey = (existingLinks ?? [])
    .map((link) => `${link.moduleType}:${link.moduleEntryId}`)
    .sort()
    .join("|");

  // Seed from what the document is already linked to, so unticking is a real removal rather than
  // the dialog forgetting state between opens.
  useEffect(() => {
    if (open) {
      setSelected(new Set(existingKey ? existingKey.split("|") : []));
    }
  }, [open, document?.id, existingKey]);

  const toggle = (key) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const groups = groupEntriesByModule(query.data);

  const handleSave = () => {
    const chosen = (query.data ?? []).filter((entry) => selected.has(entryKey(entry)));
    onSave(chosen);
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Show in module</DialogTitle>
      <DialogContent className="space-y-3">
        <Typography variant="body2" color="text.secondary">
          {document?.originalFilename} will appear inside the entries you tick. The file is not
          copied, and it stays on this request.
        </Typography>

        {query.isLoading && <Loader />}
        {query.error && <Alert severity="error">{query.error.message}</Alert>}
        {!query.isLoading && !query.error && groups.length === 0 && (
          <Alert severity="info">
            This client has no module records you can access, so there is nowhere to show it yet.
          </Alert>
        )}

        {groups.map((group) => (
          <div key={group.moduleType} className="border border-app-border rounded-lg p-3">
            <Typography variant="subtitle2">{moduleLabel(group.moduleType)}</Typography>
            {group.entries.map((entry) => (
              <div key={entryKey(entry)}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selected.has(entryKey(entry))}
                      onChange={() => toggle(entryKey(entry))}
                    />
                  }
                  label={entry.entryLabel}
                />
              </div>
            ))}
          </div>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || query.isLoading}>
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShowInModuleDialog;
